import _ from 'lodash'
import base64 from 'base-64'
import { SET_LOADING_STATUS, RESET } from './mutation-types'
import { SET_MESSAGES, APPEND_MESSAGE, UPDATE_MESSAGE, SET_SENDING_STATUS, SET_MASS_SENDING_STATUS, SET_UPLOADING_FILE }
  from './message-mutation-types'
import { UPLOAD_CONTACT_MEDIA } from '@/backend/graphql/contactGQL'
import store from '@/store'
import { apolloClient } from '@/plugins/apollo/apolloUploadClient'
import { twilioAxios } from '../../backend/vue-axios'
import { getSocket, reconnectSocket } from '@/store/modules/socketIO'
import { defaultAvatar } from '@/store/utils'

function isValidMessage (state, index) {
  return index < state.allMessages.length
}

export const isInboundMessage = (messageInstance) => {
  const direction = _.get(messageInstance, 'direction', '')
  return direction.includes('inbound')
}

function getContactMsgAuthParameters (contact) {
  const accountSid = _.get(store.getters['account/getUser'], 'twilio_sid', '')
  const authToken = store.getters['authentication/getChatAuthToken']
  const contactNumber = _.get(contact, 'number', '')
  const userNumber = _.get(store.getters['account/getUser'], 'twilio_number', '')
  return { accountSid, authToken, contactNumber, userNumber }
}

function getMassMsgAuthParameters () {
  const accountSid = _.get(store.getters['account/getUser'], 'twilio_sid', '')
  const credit = _.get(store.getters['account/getUser'], 'credit', '')
  const authToken = store.getters['authentication/getChatAuthToken']
  const userNumber = _.get(store.getters['account/getUser'], 'twilio_number', '')
  return { accountSid, authToken, credit, userNumber }
}

function shouldUpdateMessage (messageInstance) {
  if (!messageInstance) return false
  const activeContact = store.getters['messageDrawer/getItem']
  const activeContactNumber = isInboundMessage(messageInstance) ? messageInstance.from : messageInstance.to
  if (!activeContact || !activeContact.number || !activeContactNumber) return false
  return activeContactNumber.replace(' ', '') === activeContact.number.replace(' ', '')
}

async function getValidSocket (self) {
  const socket = getSocket(self)
  if (!socket.connected) {
    await reconnectSocket(socket)
  }
  return socket
}

const state = {
  allMessages: [],
  status: {
    loading: false,
    sending: false,
    massSending: false,
    uploadingFile: false
  }
}

const getters = {
  getLoadingStatus: (state) => {
    return state.status.loading
  },
  getSendingStatus: (state) => {
    return state.status.sending
  },
  getMassSendingStatus: (state) => {
    return state.status.massSending
  },
  getUploadingFile: (state) => {
    return state.status.uploadingFile
  },
  getAllMessages: (state) => {
    return state.allMessages
  },
  getSelfAvatar: (state) => {
    return defaultAvatar
  },
  getMediaListByIndex: (state) => (index) => {
    return isValidMessage(state, index) ? state.allMessages[index].mediaList : []
  }
  // getContactAvatar: (state) => {
  //   return state.status.avatar
  // }
}

const requestMedia = ({ commit }, { message, index }) => {
  const accountSid = _.get(store.getters['account/getUser'], 'twilio_sid', '')
  const authToken = store.getters['authentication/getChatAuthToken']
  if (!accountSid || !authToken) return
  const baseToken = base64.encode(accountSid + ':' + authToken)
  twilioAxios.defaults.headers.common['Authorization'] = `Basic ${baseToken}`
  const mediaUrl = message.media || message.subresourceUris.media
  twilioAxios.get(mediaUrl)
    .then(request => {
      commit(UPDATE_MESSAGE, {
        index,
        sid: message.sid,
        keys: ['mediaList', 'height'],
        values: [request.data.media_list, 335] })
    }
    )
    .catch(error => {
      console.error(error)
    })
}

const actions = {
  setLoadingStatus ({ commit }, { val }) {
    commit(SET_LOADING_STATUS, { val })
  },
  reset ({ commit }) {
    commit(RESET)
  },
  setSendingStatus ({ commit }, { val }) {
    commit(SET_SENDING_STATUS, { val })
  },
  setMassSendingStatus ({ commit }, { val }) {
    commit(SET_MASS_SENDING_STATUS, { val })
  },
  setUploadingFile ({ commit }, { val }) {
    commit(SET_UPLOADING_FILE, { val })
  },
  async sendMessage ({ commit }, { msgBody, mediaUrl, contact }) {
    commit(SET_SENDING_STATUS, { val: true })
    const { accountSid, authToken, contactNumber, userNumber } = getContactMsgAuthParameters(contact)
    if (!accountSid || !authToken || !contactNumber || !userNumber) {
      store.dispatch('alert/error', 'Authentication Error')
      commit(SET_SENDING_STATUS, { val: false })
      return
    }

    const socket = await getValidSocket(this)
    socket.emit('sendMessage', {
      accountSid,
      authToken,
      fromNumber: userNumber,
      toNumber: contactNumber,
      msgBody,
      mediaUrl }, (resp) => {
      if (resp.error) {
        console.error('resp.error', resp)
        store.dispatch('alert/error', resp.error.message ? resp.error.message : 'Send Message Error!')
        commit(SET_SENDING_STATUS, { val: false })
      }
    })
  },
  async sendMMS ({ commit }, { msgBody, file, contact }) {
    commit(SET_SENDING_STATUS, { val: true })
    commit(SET_UPLOADING_FILE, { val: true })
    try {
      const res = await apolloClient.mutate({
        mutation: UPLOAD_CONTACT_MEDIA,
        variables: { id: contact.id, file }
      })
      commit(SET_UPLOADING_FILE, { val: false })
      const mediaUrl = _.get(res, 'data.uploadContactMedia.mediaUrl', '')
      if (!mediaUrl) {
        store.dispatch('alert/error', 'Authentication Error')
        commit(SET_SENDING_STATUS, { val: false })
        return
      }
      store.dispatch('message/sendMessage', { msgBody, mediaUrl, contact })
    } catch (error) {
      commit(SET_SENDING_STATUS, { val: false })
      commit(SET_UPLOADING_FILE, { val: false })
      store.dispatch('alert/error', error.message)
    }
  },
  async sendMassMessages ({ commit }, {
    msgBody, mediaUrl,
    selectAll, selectedIds, unselectedIds, queryName, variables, contactsCount, pendingType,
    startTime, endTime, timeZone }) {
    commit(SET_MASS_SENDING_STATUS, { val: true })
    const { accountSid, authToken, credit, userNumber } = getMassMsgAuthParameters()
    if (!accountSid || !authToken || !userNumber) {
      store.dispatch('alert/error', 'Authentication Error')
      commit(SET_MASS_SENDING_STATUS, { val: false })
      return
    }
    if (credit < contactsCount) {
      store.dispatch('alert/error', 'Not enough credits')
      commit(SET_MASS_SENDING_STATUS, { val: false })
      return
    }
    const queryVariables = selectAll ? {
      queryName,
      variables,
      unselectedIds: Array.from(unselectedIds)
    } : {}

    const socket = await getValidSocket(this)
    socket.emit('sendMassMessages', {
      accountSid,
      authToken,
      fromNumber: userNumber,
      selectedIds: Array.from(selectedIds),
      contactsCount,
      queryVariables,
      pendingType,
      startTime,
      endTime,
      timeZone,
      msgBody,
      mediaUrl }, (resp) => {
      if (resp.error) {
        console.error('resp.error', resp)
        store.dispatch('alert/error', resp.error.message ? resp.error.message : 'Send Message Error!')
      }
      commit(SET_MASS_SENDING_STATUS, { val: false })
      const messageData = resp.data.messageData
      store.dispatch('contact/updateContactsPendingStatus', { messageData })
      store.dispatch('alert/success', `Created ${messageData.length} messages successfully`)
    })
  },
  async sendMassMMS ({ commit }, { msgBody, file,
    selectAll, selectedIds, unselectedIds, queryName, variables, contactsCount, pendingType,
    contactId, startTime, endTime, timeZone }) {
    commit(SET_MASS_SENDING_STATUS, { val: true })
    commit(SET_UPLOADING_FILE, { val: true })
    try {
      const res = await apolloClient.mutate({
        mutation: UPLOAD_CONTACT_MEDIA,
        variables: { id: contactId, file }
      })
      commit(SET_UPLOADING_FILE, { val: false })
      const mediaUrl = _.get(res, 'data.uploadContactMedia.mediaUrl', '')
      if (!mediaUrl) {
        store.dispatch('alert/error', 'Authentication Error')
        commit(SET_MASS_SENDING_STATUS, { val: false })
        return
      }
      store.dispatch('message/sendMassMessages', {
        msgBody,
        mediaUrl,
        selectAll,
        selectedIds,
        unselectedIds,
        queryName,
        variables,
        contactsCount,
        pendingType,
        startTime,
        endTime,
        timeZone
      })
    } catch (error) {
      commit(SET_MASS_SENDING_STATUS, { val: false })
      commit(SET_UPLOADING_FILE, { val: false })
      store.dispatch('alert/error', error.message)
    }
  },
  async pullMessages ({ commit }, { init, contact, outbondOpts, inbondOpts }) {
    commit(SET_LOADING_STATUS, { init, val: true })
    const { accountSid, authToken, contactNumber, userNumber } = getContactMsgAuthParameters(contact)
    if (!accountSid || !authToken || !contactNumber || !userNumber) {
      store.dispatch('alert/error', 'Authentication Error')
      commit(SET_LOADING_STATUS, { val: false })
      return
    }

    const socket = await getValidSocket(this)
    const opt = Object.assign({ pageSize: '30' }, outbondOpts, inbondOpts)
    socket.emit('pullMessages', { accountSid, authToken, number: contactNumber, opt }, (resp) => {
      if (resp.error) {
        store.dispatch('alert/error', resp.error.message ? resp.error.message : 'Pull Messages Error!')
      } else {
        commit(SET_MESSAGES, { commit, data: resp.data })
      }
      commit(SET_LOADING_STATUS, { val: false })
    })
  },
  async appendMessage ({ commit }, { data }) {
    commit(APPEND_MESSAGE, { commit, data })
    const smsInstance = _.get(data, 'message', null)
    const statusCode = _.get(data, 'statusCode', null)
    store.dispatch('contact/updateContactMessage', { smsInstance, statusCode })
    commit(SET_SENDING_STATUS, { val: false })
  },
  async init ({ commit }, { data }) {
    // commit(SET_LOADING_STATUS, { val: false })
    // commit(INIT, { data })
  },
  async updateMessage ({ commit }, { message, index, key, val }) {
    commit(UPDATE_MESSAGE, {
      index,
      sid: message.sid,
      keys: [key],
      values: [val] })
  }
}

const mutations = {
  [SET_LOADING_STATUS] (state, { init, val }) {
    state.status.loading = val
    if (init) {
      state.allMessages = []
    }
  },
  [SET_SENDING_STATUS] (state, { val }) {
    state.status.sending = val
    if (val) {
      store.dispatch('messageDrawer/setForcedScrollBottom', { val })
    }
  },
  [SET_MASS_SENDING_STATUS] (state, { val }) {
    state.status.massSending = val
  },
  [SET_UPLOADING_FILE] (state, { val }) {
    state.status.uploadingFile = val
  },
  [SET_MESSAGES] (state, { commit, data }) {
    if (!data || !_.get(data, 'messages', '')) return
    let messages = data.messages.inbound.messages.concat(data.messages.outbound.messages)
    messages.sort((a, b) => {
      return new Date(a.dateSent).getTime() - new Date(b.dateSent).getTime()
    })

    state.allMessages = messages.map((message, index) => {
      const hasMedia = _.get(message, 'numMedia', 0) > 0
      if (hasMedia) {
        requestMedia({ commit }, { message, index })
      }
      return {
        id: index,
        sid: message.sid,
        body: message.body,
        self: !message.is_inbound,
        date: new Date(message.dateSent),
        hasMedia: hasMedia,
        mediaList: [],
        height: hasMedia ? 400 : 36
      }
    })
  },
  [APPEND_MESSAGE] (state, { commit, data }) {
    if (!data || !data.message || !shouldUpdateMessage(data.message)) return
    const hasMedia = _.get(data.message, 'numMedia', 0) > 0
    if (hasMedia) {
      requestMedia({ commit }, { message: data.message, index: state.allMessages.length })
    }
    const message = Object.assign({}, {
      id: state.allMessages.length,
      sid: data.message.sid,
      body: data.message.body,
      self: !isInboundMessage(data.message),
      date: new Date(data.message.dateSent ? data.message.dateSent : data.message.dateCreated),
      hasMedia: hasMedia,
      mediaList: [],
      height: hasMedia ? 400 : 36
    })
    state.allMessages.push(message)
  },
  [UPDATE_MESSAGE] (state, { index, sid, keys, values }) {
    const message = _.get(state, `allMessages[${index}]`, null)
    if (message && message.sid === sid) {
      keys.forEach((key, keyIndex) => {
        if (message.hasOwnProperty(key)) { message[key] = values[keyIndex] }
      })
    }
  },
  [RESET] (state) {
    state.allMessages = []
  }
}

export const massMessagePendingType = {
  normal: 0,
  send: 1,
  reply: 2
}

export const message = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
